/**
 * ScopeBadge — small pill that tells the user which slice of data they're
 * looking at (Org-wide, their team, just them). Surfaced next to widgets
 * that are auto-scoped by the API (deal-risk, pipeline-velocity, follow-up).
 */
import { clsx } from 'clsx';

interface ScopeShape {
  level: 'org' | 'team' | 'self' | 'none';
  label: string;
  isOrgWide: boolean;
  departmentNames?: string[];
}

export function ScopeBadge({ scope, className }: { scope: ScopeShape | undefined; className?: string }) {
  if (!scope) return null;

  // Don't bother showing a badge for org-wide — that's the implicit default
  // for owners/admins, and surfacing it everywhere is noisy.
  if (scope.isOrgWide) return null;

  const tone =
    scope.level === 'team' ? 'border-primary/30 bg-primary/5 text-primary'
    : scope.level === 'self' ? 'border-border bg-surface-2 text-ink-muted'
    : 'border-warn/30 bg-warn/5 text-warn';

  const icon =
    scope.level === 'team' ? (
      <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8zM23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></svg>
    ) : scope.level === 'self' ? (
      <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="8" r="4" /><path d="M4 21v-2a6 6 0 016-6h4a6 6 0 016 6v2" /></svg>
    ) : (
      <svg aria-hidden="true" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10" /><line x1="4.93" y1="4.93" x2="19.07" y2="19.07" /></svg>
    );

  const title =
    scope.level === 'none' ? 'No attribution emails set — talk to your admin'
    : scope.level === 'team' ? `Showing data for: ${(scope.departmentNames ?? []).join(', ') || 'your team'}`
    : 'Showing only your own attributed data';

  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wider',
        tone,
        className,
      )}
      title={title}
    >
      {icon}
      <span>{scope.label}</span>
    </span>
  );
}
